import { Check, Gear, Info } from "@phosphor-icons/react"

import { IconButton } from "@tokenterminal/core/ui/button/IconButton"
import { Heading } from "@tokenterminal/core/ui/heading/Heading"
import {
  ToggleButton,
  ToggleButtonGroup,
} from "@tokenterminal/core/ui/input/toggle/ToggleButtonGroup"
import { Card } from "@tokenterminal/core/ui/layout/card/Card"
import { MenuButton } from "@tokenterminal/core/ui/menu/MenuButton"
import { Tooltip } from "@tokenterminal/core/ui/overlays/tooltip/Tooltip"
import { type Interval } from "@tokenterminal/tt-analytics-api-types/dist/api/zoom"
import { useMemo, useState } from "react"
import { type MetricConfiguration } from "utils/trpc"
import { MarketSectorsTreemap } from "./MarketSectorsTreemap"
import { useTreemapData } from "./useTreemapData"

const makeSubtitle = ({
  interval,
  metricConfiguration,
}: {
  interval: string
  metricConfiguration: MetricConfiguration | undefined
}) => {
  const convertedInterval = interval.replace("d", " days")

  const intervalName =
    interval === "max"
      ? "all time"
      : interval === "ytd"
        ? "the year to date"
        : interval === "24h"
          ? "the last 24 hours"
          : `the last ${convertedInterval}`

  return (
    <>
      Market sectors ranked by the sum of{" "}
      {metricConfiguration?.static
        ? "the latest day's value for all projects in each sector across all chains"
        : `all projects' ${metricConfiguration?.title.toLowerCase()} in each sector across all chains`}
      {metricConfiguration?.static ? (
        ""
      ) : (
        <>
          {" "}
          for {interval === "ytd" ? "" : interval === "max" ? "" : "the last "}
          {intervalName}
        </>
      )}
    </>
  )
}

const makeSubheading = ({
  interval,
  metricConfiguration,
  colorMode,
}: {
  interval: string
  metricConfiguration: MetricConfiguration
  colorMode: "trend" | "categorical"
}) => {
  const convertedInterval = interval.replace("d", " day")

  const intervalName =
    interval === "max"
      ? "All time"
      : interval === "ytd"
        ? "YTD"
        : interval === "24h"
          ? "24 hour"
          : `${convertedInterval}`

  if (colorMode === "trend") {
    return `${metricConfiguration.static ? `Latest value` : `${intervalName} sum `} (${intervalName.toLowerCase()} change)`
  }

  return `${metricConfiguration.static ? `Latest value` : `${intervalName} sum `} (Market share)`
}

const SettingsMenuButton = ({
  colorMode,
  setColorMode,
}: {
  colorMode: "trend" | "categorical"
  setColorMode: (colorMode: "trend" | "categorical") => void
}) => {
  return (
    <>
      <ToggleButtonGroup
        selectedKey={colorMode}
        onSelectionChange={(key) =>
          setColorMode(key as "trend" | "categorical")
        }
        className="hidden md:flex"
      >
        <ToggleButton id="trend">% Change</ToggleButton>
        <ToggleButton id="categorical">% Market share</ToggleButton>
      </ToggleButtonGroup>
      <MenuButton
        trigger={
          <IconButton
            icon={Gear}
            variant="ghost"
            label="Settings"
            className="-mt-2 -mr-2 flex md:hidden"
          />
        }
        placement="bottom end"
      >
        <MenuButton.Section>
          <MenuButton.Header>Display percentage as</MenuButton.Header>
          <MenuButton.Item
            onAction={() => setColorMode("trend")}
            closeOnSelect={false}
          >
            <div className="flex flex-row items-center gap-2 pr-1">
              <Check
                size={16}
                className={colorMode === "trend" ? "opacity-100" : "opacity-0"}
              />
              % Change
            </div>
          </MenuButton.Item>
          <MenuButton.Item
            onAction={() => setColorMode("categorical")}
            closeOnSelect={false}
          >
            <div className="flex flex-row items-center gap-2 pr-1">
              <Check
                size={16}
                className={
                  colorMode === "categorical" ? "opacity-100" : "opacity-0"
                }
              />
              % Market share
            </div>
          </MenuButton.Item>
        </MenuButton.Section>
      </MenuButton>
    </>
  )
}

export const MarketSectorsTreemapCard = ({
  metricConfiguration,
  interval,
}: {
  interval: Interval
  metricConfiguration: MetricConfiguration
}) => {
  const selectedMetricId = metricConfiguration.metric_id
  const selectedMetricTitle = metricConfiguration.title
  const [colorMode, setColorMode] = useState<"trend" | "categorical">("trend")

  const { data: treemapData = [], isLoading } = useTreemapData({
    selectedMetricId,
    interval,
  })

  const longerSubtitle = useMemo(() => {
    return makeSubtitle({
      interval,
      metricConfiguration,
    })
  }, [interval, metricConfiguration])

  const subheading = useMemo(() => {
    return makeSubheading({
      interval,
      metricConfiguration,
      colorMode,
    })
  }, [interval, metricConfiguration, colorMode])
  return (
    <Card className="w-full rounded-xl p-6">
      <div className="flex justify-between items-center mb-4">
        <div>
          <div className="flex flex-row items-center gap-2">
            <Heading level={4}>{selectedMetricTitle} by market sector</Heading>
            <Tooltip content={longerSubtitle}>
              <Tooltip.Trigger>
                <Info size={16} tabIndex={0} className="text-fg-secondary" />
              </Tooltip.Trigger>
            </Tooltip>
          </div>
          <div className="text-sm text-fg-secondary">{subheading}</div>
        </div>
        <div className="flex gap-2 items-center">
          <SettingsMenuButton
            colorMode={colorMode}
            setColorMode={setColorMode}
          />
        </div>
      </div>
      <div
        style={{
          opacity: isLoading ? 0.5 : 1,
          pointerEvents: isLoading ? "none" : "auto",
          transition: "opacity 0.1s ease-in-out",
        }}
      >
        <MarketSectorsTreemap
          colorMode={colorMode}
          height={250}
          treeMapData={treemapData}
          metricFormat={metricConfiguration.format}
        />
      </div>
    </Card>
  )
}
